import React from "react";
import { Helmet } from "react-helmet-async";
import { CONTACT, HQ, postalAddress, VOTRA_LOCATIONS } from "../data/votraLocations";

export const SITE_URL = "https://votra.dk";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/uwcrddus_Votra%20logo.png";
const LOGO_URL = `${SITE_URL}/favicon-512.png`;
export const ORG_ID = `${SITE_URL}/#organization`;

// Organization / LocalBusiness structured data — helps brand ("Votra") ranking + knowledge panel.
// Kun Votras EGNE adresser udstilles som LocalBusiness. Undervisning foregår også hos
// samarbejdspartneren UCplus (Skovlunde, Højbjerg) — de er partnerens adresser og må
// ikke præsenteres som Votras forretningssteder. Se data/votraLocations.js.
const _locationEntity = (l) => ({
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/lokationer/${l.pageSlug}#${l.id}`,
  "name": l.name,
  "url": `${SITE_URL}/lokationer/${l.pageSlug}`,
  "image": LOGO_URL,
  "logo": LOGO_URL,
  "telephone": CONTACT.phone,
  "email": CONTACT.email,
  "address": postalAddress(l),
  "parentOrganization": { "@id": ORG_ID },
});

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "@id": ORG_ID,
  "name": CONTACT.brandName,
  "alternateName": ["Votra", "Votra Danmark ApS"],
  "legalName": CONTACT.legalName,
  "url": SITE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": LOGO_URL,
    "width": 512,
    "height": 512,
  },
  "image": DEFAULT_IMAGE,
  "description": "Votra Danmark ApS tilbyder vognmandsuddannelser til lastbil og varebil, taxauddannelse, varebilschauffør (VB GU/VB EU), BAB-flextrafik samt IT- og AI-forløb. Hovedkontor i Søborg, undervisning i Søborg og Ringsted, og i samarbejde med UCplus i Skovlunde og Højbjerg.",
  "email": CONTACT.email,
  "telephone": CONTACT.phone,
  "vatID": CONTACT.vatId,
  "taxID": CONTACT.cvr,
  "areaServed": "DK",
  "address": postalAddress(HQ),
  "location": VOTRA_LOCATIONS.map(_locationEntity),
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": CONTACT.phone,
    "email": CONTACT.email,
    "contactType": "customer service",
    "areaServed": "DK",
    "availableLanguage": ["Danish", "English", "Arabic", "Ukrainian"],
  },
  "sameAs": ["https://www.trustpilot.com/review/votra.dk"],
  "knowsAbout": [
    "Vognmandsuddannelse til lastbil",
    "Vognmandsuddannelse til varebil",
    "Taxauddannelse og chaufførkort",
    "Varebil Grunduddannelse (VB GU)",
    "Varebil Efteruddannelse (VB EU)",
    "BAB og flextrafik",
    "Evakueringsstol (EVS)",
    "AI- og IT-forløb for transportbranchen",
  ],
};

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": it.name,
    "item": `${SITE_URL}${it.path}`,
  })),
});

/** "10 dage" → "P10D", "6 uger" → "P6W". Udokumenterede varigheder udelades. */
const isoDuration = (text) => {
  const m = /(\d+)\s*(dag|dage|uge|uger|time|timer)/i.exec(text || "");
  if (!m) return undefined;
  const n = m[1];
  const unit = m[2].toLowerCase();
  if (unit.startsWith("uge")) return `P${n}W`;
  if (unit.startsWith("time")) return `PT${n}H`;
  return `P${n}D`;
};

export const courseJsonLd = ({
  name, description, path, priceLow, priceHigh, duration, languages, credential,
  prerequisites, audience, teaches, instances, courseMode,
}) => {
  const node = {
    "@context": "https://schema.org",
    "@type": "Course",
    "@id": `${SITE_URL}${path}#course`,
    "name": name,
    "description": description,
    "url": `${SITE_URL}${path}`,
    "provider": { "@id": ORG_ID },
    "inLanguage": languages?.length ? languages : ["da"],
  };
  if (courseMode) node.courseMode = courseMode;
  const iso = isoDuration(duration);
  if (iso) node.timeRequired = iso;
  if (credential) node.educationalCredentialAwarded = credential;
  if (prerequisites?.length) node.coursePrerequisites = prerequisites;
  if (audience) node.audience = { "@type": "Audience", "audienceType": audience };
  if (teaches?.length) node.teaches = teaches;
  if (typeof priceLow === "number" && priceLow > 0) {
    node.offers = {
      "@type": "Offer",
      "priceCurrency": "DKK",
      "url": `${SITE_URL}${path}`,
      "availability": "https://schema.org/InStock",
      "category": "Kursusgebyr",
      ...(priceHigh && priceHigh !== priceLow
        ? { "priceSpecification": { "@type": "PriceSpecification", "minPrice": priceLow, "maxPrice": priceHigh, "priceCurrency": "DKK" } }
        : {}),
      "price": priceLow,
    };
  }
  if (instances?.length) node.hasCourseInstance = instances;
  return node;
};

export const faqJsonLd = (faqs, id) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  ...(id ? { "@id": id } : {}),
  "mainEntity": faqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
});

/** Kursuskataloget som ItemList, udstilles kun på /kurser, aldrig globalt. */
export const courseCatalogJsonLd = (groups) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${SITE_URL}/kurser#catalog`,
  "name": "Votra kursuskatalog",
  "itemListOrder": "https://schema.org/ItemListUnordered",
  "numberOfItems": groups.length,
  "itemListElement": groups.map((g, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "item": {
      "@type": "Course",
      "@id": `${SITE_URL}/kurser/${g.slug}#course`,
      "name": g.title,
      "description": g.shortDescription || g.description,
      "url": `${SITE_URL}/kurser/${g.slug}`,
      "provider": { "@id": ORG_ID },
      "inLanguage": g.hasLanguageVariants ? ["da", "en", "ar", "uk"] : ["da"],
      ...(isoDuration(g.duration) ? { "timeRequired": isoDuration(g.duration) } : {}),
    },
  })),
});

const BRANDS = {
  education: {
    siteName: "Votra Vognmandsuddannelser",
    image: DEFAULT_IMAGE,
  },
  business: {
    siteName: "Votra Business",
    image: "https://customer-assets.emergentagent.com/job_b2ca0c7b-d03f-4a16-bd13-4c17923effbc/artifacts/i1zeqilg_4.png",
  },
};

export const Seo = ({ title, description, path = "", noindex = false, jsonLd = null, image, lang = "da", locale = "da_DK", alternates = [], brand = "education" }) => {
  const url = `${SITE_URL}${path}`;
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const theme = BRANDS[brand] || BRANDS.education;
  const ogImage = image || theme.image;

  // JSON-LD renderes nu direkte i komponenttræet i stedet for at blive injiceret i
  // <head> fra en useEffect. Effekter kører ikke på build-tid, så schemaet manglede
  // i den statiske HTML, en klient uden JavaScript får. Google læser JSON-LD både i
  // <head> og i <body>. Blokkene ligger inde i #root, så React rydder dem selv op
  // ved mount, og der kan derfor ikke opstå dobbelt schema.
  React.useEffect(() => {
    document.head.querySelectorAll("title").forEach((t, i) => { if (i > 0) t.remove(); });
  }, []);

  return (
    <>
      <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {alternates.map((a) => (
        <link key={`${a.hrefLang}-${a.href}`} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
      ))}
      {noindex
        ? <meta name="robots" content="noindex, follow" />
        : <meta name="robots" content="index, follow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={theme.siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={locale} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
      {blocks.map((b, i) => (
        <script
          key={i}
          type="application/ld+json"
          data-votra-ld="1"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(b) }}
        />
      ))}
    </>
  );
};


// Reusable visible SEO content + FAQ block (renders just above the footer).
import { Overline, Reveal, PRIMARY } from "./shared/kursusUi";
export const SeoContentSection = ({ intro, faq, testid }) => (
  <section className="bg-white border-t border-slate-100" data-testid={testid}>
    <div className="container mx-auto px-4 md:px-6 py-20 max-w-4xl">
      {intro && (
        <>
          <Reveal>
            <Overline>Godt at vide</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-medium mb-6 text-slate-900 leading-tight">{intro.heading}</h2>
          </Reveal>
          <div className="space-y-5 text-slate-600 leading-relaxed text-lg">
            {intro.paragraphs.map((p, i) => <Reveal as="p" i={i} key={i}>{p}</Reveal>)}
          </div>
        </>
      )}
      {faq && faq.length > 0 && (
        <>
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-medium mt-16 mb-8 text-slate-900 leading-tight">Ofte stillede spørgsmål</h2>
          </Reveal>
          <div className="divide-y divide-slate-200 border-t border-slate-200">
            {faq.map((f, i) => (
              <Reveal i={i} key={i} className="py-6" data-testid={testid ? `${testid}-faq-${i}` : undefined}>
                <h3 className="text-lg font-semibold mb-2 flex items-start gap-3 text-slate-900">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: PRIMARY }} />
                  {f.q}
                </h3>
                <p className="text-slate-600 leading-relaxed pl-[18px]">{f.a}</p>
              </Reveal>
            ))}
          </div>
        </>
      )}
    </div>
  </section>
);
